import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { RevealText, FadeUp } from "@/components/site/Reveal";
import { IMAGES } from "@/data/images";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ForeignInvestment = () => {
  const { t, lang } = useLang();
  const data = t.foreign;

  return (
    <div data-testid="foreign-investment-page" className="pt-28 md:pt-36">
      {/* HERO */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[color:var(--sw-orange)] mb-6">{data.eyebrow}</div>
            <RevealText lines={[data.title]} as="h1" className={`display text-white ${lang === "ur" ? "text-lg sm:text-xl md:text-2xl lg:text-3xl" : lang === "en" ? "text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05]" : "text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.92]"}`} />
            <FadeUp delay={0.2}>
              <p className={`mt-10 text-white/70 max-w-2xl leading-relaxed ${lang === "ur" ? "text-base" : "text-lg md:text-xl"}`}>{data.body}</p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/contact" data-testid="fi-cta" className="btn-primary px-6 py-3.5 rounded-full inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold">
                  {data.cta} <ArrowUpRight size={16}/>
                </Link>
                <a href="https://wa.me/966590960314" target="_blank" rel="noreferrer" className="btn-ghost px-6 py-3.5 rounded-full inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em]">
                  <MessageCircle size={16}/> WhatsApp
                </a>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="md:col-span-5">
            <div className="relative spot-image rounded-md overflow-hidden aspect-[4/5]">
              <img src={IMAGES.saudi_office} alt="Saudi office" className="w-full h-full object-cover grayscale-[15%] contrast-125" />
              <div className="absolute bottom-6 left-6 right-6 text-[10px] uppercase tracking-[0.32em] text-white/70">Riyadh · KSA</div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* WHY SAUDI */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <FadeUp className="md:col-span-4">
            <div className="chapter-num text-[100px] md:text-[140px] leading-none">02</div>
            <div className="mt-4 text-[11px] uppercase tracking-[0.32em] text-white/50">{data.why.t}</div>
          </FadeUp>
          <div className="md:col-span-8">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.why.points.map((p, i) => (
                <FadeUp key={i} delay={i * 0.08}>
                  <li className="sw-card rounded-md p-8">
                    <div className="chapter-num text-2xl mb-4">{String(i + 1).padStart(2, "0")}</div>
                    <div className={`text-white/85 ${lang === "ur" ? "text-base" : "text-lg"}`}>{p}</div>
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* OFFERING */}
      <section className="border-t border-white/10 bg-[color:var(--sw-surface)]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            <FadeUp className="md:col-span-6">
              <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-6">03 — {data.offering.t}</div>
              <RevealText lines={[data.offering.t]} as="h2" className="display text-3xl md:text-5xl text-white leading-tight" />
            </FadeUp>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.offering.items.map((it, i) => (
              <FadeUp key={i} delay={i * 0.06}>
                <li className="flex items-baseline gap-6 border-b border-white/10 pb-4 group">
                  <span className="chapter-num text-sm w-10">{String(i + 1).padStart(2, "0")}</span>
                  <span className={`text-white/85 group-hover:text-[color:var(--sw-orange)] transition-colors ${lang === "ur" ? "text-base" : "text-lg md:text-xl"}`}>{it}</span>
                </li>
              </FadeUp>
            ))}
          </ul>
        </div>
      </section>

      {/* STEPS */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-40">
        <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-6">04 — {data.steps.t}</div>
        <RevealText lines={[data.steps.t]} as="h2" className="display text-3xl md:text-5xl text-white leading-tight mb-12" />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {data.steps.items.map((s, i) => (
            <FadeUp key={i} delay={i * 0.08}>
              <div className="sw-card rounded-md p-6 md:p-8 h-full">
                <div className="chapter-num text-3xl md:text-4xl">{s.n}</div>
                <div className="display text-xl md:text-2xl text-white mt-4 mb-2">{s.t}</div>
                <div className="text-white/60 text-sm leading-relaxed">{s.d}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* DOCS + DURATION */}
      <section className="border-t border-white/10">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
          <FadeUp className="md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-6">05 — {data.docs.t}</div>
            <RevealText lines={[data.docs.t]} as="h2" className="display text-3xl md:text-5xl text-white leading-tight mb-8" />
            <ul className="space-y-4">
              {data.docs.items.map((d, i) => (
                <FadeUp key={i} delay={i * 0.05}>
                  <li className="flex items-baseline gap-6 border-b border-white/10 pb-3">
                    <span className="chapter-num text-sm w-10">{String(i + 1).padStart(2, "0")}</span>
                    <span className={`text-white/85 ${lang === "ur" ? "text-base" : "text-lg"}`}>{d}</span>
                  </li>
                </FadeUp>
              ))}
            </ul>
          </FadeUp>
          <FadeUp delay={0.15} className="md:col-span-5">
            <div className="sw-card rounded-md p-10 h-full flex flex-col justify-center">
              <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-4">06 — {data.duration.t}</div>
              <div className="display text-4xl md:text-5xl text-white leading-tight">{data.duration.d}</div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-[1200px] px-6 md:px-10 py-24 md:py-40">
        <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-6">07 — FAQ</div>
        <RevealText lines={[data.faq.t]} as="h2" className="display text-3xl md:text-5xl text-white leading-tight mb-10" />
        <Accordion type="single" collapsible className="w-full" data-testid="fi-faq">
          {data.faq.items.map((q, i) => (
            <AccordionItem value={`item-${i}`} key={i} className="border-white/10">
              <AccordionTrigger className={`text-white text-start hover:no-underline hover:text-[color:var(--sw-orange)] ${lang === "ur" ? "text-base" : "display text-xl md:text-2xl"} py-6`}>
                <span className="flex items-baseline gap-6">
                  <span className="chapter-num text-sm">{String(i + 1).padStart(2, "0")}</span>
                  <span>{q.q}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-white/70 leading-relaxed text-base md:text-lg pb-8">
                {q.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-[color:var(--sw-surface)]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-16 md:py-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <RevealText lines={[data.cta]} as="h3" className="display text-3xl md:text-4xl text-white" />
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary px-6 py-3.5 rounded-full inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em]" data-testid="fi-cta-2">{t.nav.requestService}</Link>
            <a href="tel:+966590960314" target="_blank" rel="noreferrer" className="btn-ghost px-5 py-3 rounded-full inline-flex items-center gap-2"><Phone size={16}/> 0590960314</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForeignInvestment;
